import { ApolloError } from "@apollo/client";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  AnalyticsResultDto,
  AndFilter,
  AnyFilterExpressionInput,
  Attribute,
  AttributeType,
  BooleanOp,
  DeviceMeasurement,
  Entry_String_AttributeData_String,
  Field,
  FilterOp,
  MaintenanceStatus,
  MdmDeviceDto,
  MdmObjectDto,
  MdmObjectJobDto,
  MdmProjectConfiguration,
  SortDirection,
  SortInput,
  StatusType
} from "gql/generated/types";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import React from "react";
import { FieldErrors, FieldValues, GlobalError, Path } from "react-hook-form";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { findObjectVal, hasDecimals } from "utils/utils";
import { v4 as uuid } from "uuid";
import DeviceStatusTag from "../components/DeviceStatus";
import { CHUNK_SIZE, DATA_EXPORT_LIMIT, maintenanceStatuses, priorityType, statusType } from "./const";
import { CustomRowData, FilterInternal, MdmTableName, PriorityInfo, StatusInfo } from "./types";

export const getFormErrorMessage = (name: keyof FieldValues, errors: FieldErrors<FieldValues>) => {
  const error = findObjectVal(errors, name) as GlobalError;
  if (Array.isArray(error)) {
    return (
      <small className="p-error">
        {error.filter(Boolean)[0]?.message as string}
      </small>
    );
  }
  return error?.message ? (
    <small className="p-error">{error.message as string}</small>
  ) : (
    <small className="p-error">&nbsp;</small>
  );
};

export const successToaster = (msg: string) =>
  easasAlert({
    severity: "success",
    summary: T("alert.success"),
    detail: msg,
    life: 2000,
  });

export const errorToaster = (msg: string) => {
  easasAlert({ severity: "error", summary: T("alert.error"), detail: msg, life: 3000 });
};

export const warningToaster = (msg: string) => {
  easasAlert({ severity: "warn", summary: T("alert.warning"), detail: msg, life: 3000 });
};

export const apolloErrorToaster = (err: ApolloError) => {
  easasAlert({ severity: "error", summary: T("alert.error"), detail: err.message, life: 3000 });
};

// -------------------------------------- Table Row Templates --------------------------------------

export const severityRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <Dropdown
      value={options.value}
      options={priorityType}
      optionLabel="translationKey"
      optionValue="value"
      itemTemplate={showOptionSeverity}
      valueTemplate={options.value && showOptionSeverity}
      onChange={e => options.filterApplyCallback(e.value)}
      placeholder={T("word.select-one")}
      showClear
    />
  );
};

export const statusRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <Dropdown
      value={options.value}
      options={statusType}
      optionLabel="translationKey"
      optionValue="value"
      itemTemplate={showOptionTag}
      valueTemplate={options.value && showOptionTag}
      onChange={e => options.filterApplyCallback(e.value)}
      placeholder={T("word.select-one")}
      showClear
    />
  );
};

export const renderTag = <T extends { status?: MaintenanceStatus | StatusType | null }>(
  rowData: T
) => {
  const status = [...statusType, ...maintenanceStatuses].find(st => st.value === rowData.status);
  if (!status) return null;
  return <Tag value={T(status.translationKey)} style={{ background: status.bgColor }} />;
};

export const showOptionTag = (option: StatusInfo) => <Tag value={T(option.translationKey)} style={{ background: option.bgColor }} />

export const renderSeverity = (rowData: MdmObjectJobDto | AnalyticsResultDto) => {
  const priority = priorityType.find(pt => pt.value === rowData.severity);
  if (!priority) return null;
  return (
    <i
      className={priority.icon}
      style={{ color: priority.severity }}
      title={T(priority.translationKey)}
    >
      <span className="pl-2 text-gray-900 p-component">{T(priority.translationKey)}</span>
    </i>
  );
};

export const showOptionSeverity = (option: PriorityInfo) => (
  <i
    className={option.icon}
    style={{ color: option.severity }}
    title={T(option.translationKey)}
  >
    <span className="pl-2 text-gray-900 p-component">{T(option.translationKey)}</span>
  </i>
);

export const renderDeviceStatus = (rowData?: MdmDeviceDto | null) => (
  <DeviceStatusTag
    status={rowData?.status.status}
    lastMeasurementDate={rowData?.status.lastMeasurementDate}
  />
);

export const renderObjectLinkInAnalytics = (rowData: AnalyticsResultDto, projectId: string) => (
  <Link
    to={`/mdm/${projectId}/objects/${rowData.mdmObject?.id}`}
    className="truncate-text block"
    title={rowData.mdmObject?.title ?? ""}
  >
    {rowData.mdmObject?.title}
  </Link>
);

export const renderObjectLinkInDevices = (rowData: MdmDeviceDto, projectId: string) => (
  <Link to={`/mdm/${projectId}/objects/${rowData.objectRelation?.mdmObject?.id}`}>
    {rowData.objectRelation?.mdmObject?.identifier}
  </Link>
);

export const renderDeviceLink = (rowData: AnalyticsResultDto, projectId: string) => (
  <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`}>
    {rowData.mdmDevice?.identifier}
  </Link>
);

export const rowDropdownFilterTemplate = (
  options: ColumnFilterElementTemplateOptions,
  dropdownOptions: any[]
) => {
  return (
    <Dropdown
      value={options.value}
      options={dropdownOptions}
      onChange={e => options.filterApplyCallback(e.value)}
      placeholder={T("word.select-one")}
      showClear
    />
  );
};

export const renderLastMeasurementValue = (data: MdmDeviceDto, key: string) => {
  const value = (data.lastMeasurements as unknown as Record<string, number>)[key];
  if (hasDecimals(value)) {
    return key === "Darbo valandos" ? value.toFixed(0) : value.toFixed(2);
  }
  return value ?? "-";
};

export const renderCustomFieldValue = (
  rowData: CustomRowData,
  id: keyof CustomRowData,
  customColumns: Attribute[]
) => {
  const customAttribute = customColumns.filter(col => col?.id === id)[0];
  if (
    id &&
    customAttribute?.type === AttributeType.Link &&
    rowData[id]?.value &&
    customAttribute.reading
  ) {
    return (
      <a
        href={customAttribute.reading?.replace("{id}", rowData[id]?.value?.toString()!)}
        rel="noreferrer"
        target="_blank"
      >
        {rowData[id]?.value}
      </a>
    );
  }
  return rowData[id]?.value ?? "-";
};

export const showOptionDeviceStatus = (option: { key: string; label: string }) => {
  switch (option.key) {
    case "OFFLINE":
      return <Tag value="Offline" severity="danger" />;
    case "ONLINE":
      return <Tag value="Online" severity="success" />;
    case "NO_DATA":
      return <Tag value="N/A" className="bg-gray-500" />;
    default:
      return null;
  }
}

export const showSelectedDeviceStatus = (option: string) => {
  switch (option) {
    case "ONLINE":
      return "Online"
    case "OFFLINE":
      return "Offline";
    case "NO_DATA":
      return "N/A";
    default:
      return null;
  }
}

// -------------------------------------- Filter Templates --------------------------------------

export const searchAnalyticsFilter = (value: string): AnyFilterExpressionInput => ({
  op: BooleanOp.Or,
  left: {
    op: BooleanOp.Or,
    left: {
      op: BooleanOp.Or,
      left: {
        value,
        cond: FilterOp.Contains,
        property: "description",
      },
      right: {
        value,
        cond: FilterOp.Contains,
        property: "mdmObject.address.fullAddress",
      },
    },
    right: {
      op: BooleanOp.Or,
      left: {
        value,
        cond: FilterOp.Contains,
        property: "device.identifier",
      },
      right: {
        value,
        cond: FilterOp.Contains,
        property: "mdmObject.identifier",
      },
    },
  },
  right: {
    op: BooleanOp.Or,
    left: {
      value,
      cond: FilterOp.Contains,
      property: "deviceType",
    },
    right: {
      value,
      cond: FilterOp.Contains,
      property: "severity",
    },
  },
});

export const searchDeviceOrObjectFilter = (
  value: string,
  tableName: MdmTableName
): AnyFilterExpressionInput => ({
  op: BooleanOp.Or,
  left: {
    op: BooleanOp.Or,
    left: {
      value,
      cond: FilterOp.Contains,
      property:
        tableName === "Devices"
          ? "objectRelation.mdmObject.address.fullAddress"
          : "address.fullAddress",
    },
    right: {
      value,
      cond: FilterOp.Contains,
      property: tableName === "Devices" ? "config.title" : "title",
    },
  },
  right: {
    value,
    cond: FilterOp.Contains,
    property: "identifier",
  },
});

// -------------------------------------- Utils --------------------------------------

type NestedField = Field & { children?: NestedField[]; data: Field[]; property: string };

export const buildNestedFields = (
  containerType: string,
  fields: Field[] = [],
  key: string = "",
  data: Field[] = []
): NestedField[] => {
  return fields
    .filter(field => field.containerType === containerType)
    .map((field: Field, i) => {
      const k = key === "" ? `${i}` : `${key}-${i}`;
      if (field.leaf) {
        return {
          ...field,
          label: T(field.label as StringKey, { defaultValue: field.label }),
          data: [...data, field],
          key: k,
          property: field.key,
        };
      }
      return {
        ...field,
        label: T(field.label as StringKey, { defaultValue: field.label }),
        property: field.key,
        data: [...data, field],
        key: k,
        children: buildNestedFields(field.fieldType!, fields, k, [...data, field]),
      };
    });
};

export const formatAndFilter = (
  andFilters: AndFilter[] | undefined,
  treeOptions: NestedField[]
): FilterInternal[] => {
  return (
    andFilters?.map((f: AndFilter) => {
      const fields = findByPropertyPath(treeOptions, f.property)?.data ?? [];
      return {
        cond: f.cond,
        fields,
        value: f.value?.toString()?.includes("|") ? f.value?.split("|")! : f.value!,
        id: uuid(),
        label: fields[0]?.label ?? "",
      };
    }) ?? []
  );
};

export const findObjectValue = <T,>(object: T, key: Path<T> | undefined): unknown => {
  if (key && key.includes(".")) {
    const [key1, ...restKeys] = key.split(".");
    if (Object.prototype.hasOwnProperty.call(object, "mdmObject")) {
      const value = findObjectValue(
        (object as { mdmObject: MdmObjectDto }).mdmObject,
        key as Path<MdmObjectDto>
      );
      if (value) return value;
    }
    if (Object.prototype.hasOwnProperty.call(object, "mdmDevice")) {
      const value = findObjectValue(
        (object as { mdmDevice: MdmDeviceDto }).mdmDevice,
        key as Path<MdmDeviceDto>
      );
      if (value) return value;
    }
    if (Object.prototype.hasOwnProperty.call(object, "lastMeasurements")) {
      const objectVal = (
        object as { lastMeasurements: DeviceMeasurement[] }
      ).lastMeasurements?.find(item => item.mapping === restKeys[0])?.value;
      if (objectVal) return objectVal;
    }
    if (Object.prototype.hasOwnProperty.call(object, "customFields")) {
      const objectVal = (
        object as { customFields: Entry_String_AttributeData_String[] }
      ).customFields?.find(item => item.key === key1)?.value?.value;
      if (objectVal) return objectVal;
    }
    if (!object[key1 as keyof T]) return null;
    return findObjectValue(object[key1 as keyof T], restKeys.join(".") as Path<T[keyof T]>);
  }
  return object[key as unknown as keyof T];
};

export const findByKey = (data: NestedField[], key: string): NestedField | null => {
  // eslint-disable-next-line no-restricted-syntax
  for (const field of data) {
    if (field.key === key) {
      return field;
    }
    if (field.children && field.children.length > 0) {
      const found = findByKey(field.children, key);
      if (found) return found;
    }
  }
  return null;
};

export const findByPropertyPath = (
  data: NestedField[],
  propertyPath: string
): NestedField | null => {
  // eslint-disable-next-line no-restricted-syntax
  for (const field of data) {
    if (field.property === propertyPath) {
      return field;
    }
    let paths = propertyPath.split(".");
    if (paths[0] === "customFields") {
      paths = [`${paths[0]}.${paths[1]}`, ...paths.slice(2)];
    }
    if (field.property === paths[0] && field.children && field.children.length > 0) {
      const found = findByPropertyPath(field.children, paths.slice(1).join("."));
      if (found) return found;
    }
  }
  return null;
};

export const formatSort = (
  sortField: string,
  sortOrder: number | undefined | null,
  tableName: MdmTableName,
  config: MdmProjectConfiguration | undefined | null
) => {
  let property = sortField!;
  const properties = sortField.split(".");
  if (tableName === "Devices" || tableName === "Objects") {
    property =
      properties.length === 2 && properties.at(-1) === "value"
        ? `customFields.${properties[0]}`
        : sortField!;
  }
  if (
    tableName.startsWith("Analytics") &&
    properties.length === 2
  ) {
    if (properties[0] === "lastMeasurements") {
      property = `device.${properties.join(".")}`;
    }
    if (properties.at(-1) === "value") {
      if (config?.deviceConfiguration.customFields.find(f => f?.id === properties[0])) {
        property = `device.customFields.${properties[0]}`;
      }
      if (config?.objectConfiguration.customFields.find(f => f?.id === properties[0])) {
        property = `mdmObject.customFields.${properties[0]}`;
      }
    }
  }
  return [{ property, direction: sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc }];
};

export const fetchDataHelper = async (
  totalItems: number,
  sort: { property: string; direction: SortDirection }[],
  fetchData: (
    take?: number | null,
    skip?: number | null,
    sort?: SortInput[] | null,
    filters?: AnyFilterExpressionInput[] | null,
    cacheAndNetwork?: boolean,
    filter?: AnyFilterExpressionInput | null,
    isolatedFetch?: boolean
  ) => Promise<any>,
) => {
  let responseItems = [];
  if (totalItems > DATA_EXPORT_LIMIT) {
    const chunkSizes = new Array(Math.ceil(totalItems / CHUNK_SIZE))
      .fill(1)
      .map((val, ind, arr) =>
        ind === arr.length - 1 ? totalItems - ind * CHUNK_SIZE : val * CHUNK_SIZE
      );
    const results = await Promise.allSettled<Record<string, { items: any[] }>>(
      chunkSizes.map((take, index) =>
        fetchData(take, index * CHUNK_SIZE, sort, null, false, null, true)
      )
    );
    const successfulMutations = results.filter(result => result.status === "fulfilled");

    if (successfulMutations.length === results.length) {
      responseItems = successfulMutations.reduce((resp, result) => {
        if (result.status === "fulfilled") {
          const { items } = Object.values(result.value)[0];
          return [...resp, ...items];
        }
        return resp;
      }, [] as any[]);
    } else {
      errorToaster(T("error.export-failed"));
    }
  } else {
    const response: Record<string, { items: any[] }> = await fetchData(
      0,
      0,
      sort,
      null,
      false,
      null,
      true
    );
    responseItems = Object.values(response)[0]?.items || [];
  }
  return responseItems;
};

export const measurementUnits = (key: string) => {
  switch (key) {
    case "D":
      return "m³/h";
    case "P":
      return "kW";
    case "Q":
      return "kWh";
    case "V":
      return "m³";
    case "T1":
    case "T2":
      return "°C";
    default:
      return null;
  }
};

export const multiselectValue = (value: string[] | string | null) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : null;
};
